import { PermissionConfigItemEntity } from './permission-config-item.entity';

export enum Permission {
	Read = 'read',
	Write = 'write',
	Delete = 'delete',
	Export = 'export',
	Share = 'share',
	PersistAndCacheData = 'persist-and-cache-data',
	ManageLink = 'manage-link',
	ManagePermission = 'manage-permission',
	ManageDataCatalogue = 'manage-data-catalogue',
}

export type PermissionConfig = {
	permission: Permission;
	isDisabled: boolean;
	toggled: boolean;
};

interface IPermissionConfigListEntity {
	availablePermissions: Map<Permission, PermissionConfig>;
	activatePermissions: Array<Permission>;
	elements: Map<string, PermissionConfigItemEntity>;
}

const entityDefaults: IPermissionConfigListEntity = {
	availablePermissions: new Map(),
	activatePermissions: [],
	elements: new Map(),
};

export class PermissionConfigListEntity implements IPermissionConfigListEntity {
	public elements = entityDefaults.elements;
	public availablePermissions = entityDefaults.availablePermissions;
	public activatePermissions = entityDefaults.activatePermissions;

	public static build(
		availablePermissions: Array<PermissionConfig>
	): PermissionConfigListEntity {
		const inst = new PermissionConfigListEntity();
		const cols = availablePermissions.reduce((acc, curr) => {
			acc.push([curr.permission, curr]);
			return acc;
		}, new Array<[Permission, PermissionConfig]>());
		inst.availablePermissions = new Map(cols);
		inst.activatePermissions = availablePermissions
			.filter((config) => !config.isDisabled)
			.map((config) => config.permission);

		return inst;
	}

	private constructor() {}

	public setElements(
		elems: Map<string, PermissionConfigItemEntity>
	): PermissionConfigListEntity {
		this.elements = new Map(elems);
		this.checkIfAllSelected();
		return this;
	}

	public updateElement(
		key: string,
		element: PermissionConfigItemEntity
	): PermissionConfigListEntity {
		this.elements.set(key, element);
		return this;
	}

	public updatePermissionsConfig(
		permission: Permission
	): PermissionConfigListEntity {
		const allSelected = this.hasAllSelected(permission);

		const existing = this.availablePermissions.get(permission);

		if (existing && existing.toggled === allSelected) {
			return this;
		}

		this.availablePermissions.set(permission, {
			permission,
			toggled: allSelected,
			isDisabled: existing ? existing.isDisabled : false,
		});

		return this;
	}

	public updateAllElements(
		checked: boolean,
		permission: Permission
	): PermissionConfigListEntity {
		this.elements.forEach((elem) => {
			elem.updatePermission(checked, permission, this.activatePermissions);
		});
		return this;
	}

	public checkIfAllSelected(): PermissionConfigListEntity {
		this.availablePermissions.forEach((config, permission) => {
			config.toggled = this.hasAllSelected(permission);
			this.availablePermissions.set(permission, config);
		});

		this.elements.forEach((elem) => {
			elem.updateAllToggled(this.activatePermissions);
		});

		return this;
	}

	private hasAllSelected(permission: Permission): boolean {
		if (this.elements.size === 0) {
			return false;
		}
		for (const elem of this.elements.values()) {
			if (!elem.permissions.includes(permission)) {
				return false;
			}
		}
		return true;
	}
}
